'use client';

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiMinus } from "react-icons/fi";

const sections = [
  {
    title: "1. Information We Collect",
    content: `
1.1 Personal Information

We may collect personal information that you voluntarily provide, including but not limited to:
• Full name
• Email address
• Phone number
• City and address
• Login credentials (for secure account access)
• Job application details (career forms, CVs, LinkedIn profiles)
• Business or service inquiries

1.2 Automatically Collected Information

When you access our website, we may automatically collect:
• IP address
• Browser type and version
• Device and operating system
• Pages viewed, session duration, and navigation data
    `
  },
  {
    title: "2. Purpose of Data Collection",
    content: `
We process personal data for legitimate business purposes, including:
• Providing and managing our software solutions and services
• User authentication and secure login access
• Responding to inquiries, support requests, and communications
• Recruitment and employment evaluation
• Improving website functionality, performance, and user experience
• Analytics, reporting, and business insights
• Compliance with legal and regulatory obligations
    `
  },
  {
    title: "3. Cookies and Tracking Technologies",
    content: `
We use cookies and similar technologies to enhance website performance, analyze traffic, and improve user experience.

You may control or disable cookies through your browser settings; however, certain website features may not function correctly if cookies are disabled.
    `
  },
  {
    title: "4. Third-Party Services",
    content: `
NubitSoft may engage trusted third-party service providers such as analytics platforms, hosting services, and communication tools.

These providers process data in accordance with their own privacy policies and contractual obligations.

We do not control and are not responsible for third-party privacy practices.
    `
  },
  {
    title: "5. Data Sharing and Disclosure",
    content: `
We do not sell, rent, or trade personal data.

Information may only be disclosed:
• To comply with legal requirements or lawful requests
• To protect the rights, property, or safety of NubitSoft and its users
• To authorized service providers under strict confidentiality agreements
    `
  },
  {
    title: "6. Data Security",
    content: `
We implement appropriate administrative, technical, and physical safeguards to protect personal data against unauthorized access, disclosure, alteration, or destruction.

While we strive to use commercially acceptable means to protect information, no internet-based system is completely secure.
    `
  },
  {
    title: "7. User Accounts and Login Security",
    content: `
Users are responsible for maintaining the confidentiality of their account credentials.

Any activity conducted through a registered account is the responsibility of the account holder.
    `
  },
  {
    title: "8. Career Applications",
    content: `
Personal data submitted through career and recruitment forms is used strictly for hiring, evaluation, and internal HR processes.

Such data is accessed only by authorized personnel.
    `
  },
  {
    title: "9. Children’s Privacy",
    content: `
Our services are not intended for individuals under the age of 13.

We do not knowingly collect personal data from children.

Any such data identified will be promptly deleted.
    `
  },
  {
    title: "10. Your Data Protection Rights",
    content: `
Subject to applicable laws, you have the right to:
• Access your personal data
• Request correction or deletion
• Restrict or object to processing
• Withdraw consent where applicable

Requests can be submitted using the contact details provided on our website.
    `
  },
  {
    title: "11. Policy Updates",
    content: `
NubitSoft reserves the right to update this Privacy Policy at any time.

Updates will be posted on this page and will take effect immediately.
    `
  }
];


export default function PrivacyAccordion() {
  const [active, setActive] = useState(null);

  return (
    <div className="space-y-5">
      {sections.map((item, index) => {
        const isOpen = active === index;

        return (
          <div
            key={index}
            className={`relative rounded-xl overflow-hidden border 
              ${isOpen ? "border-blue-500/40" : "border-gray-800"}
              bg-[#0F172A]/80 backdrop-blur-md transition-all`}
          >
            {/* Accent Strip */}
            <div
              className={`absolute left-0 top-0 h-full w-1 transition-all
                ${isOpen ? "bg-blue-500" : "bg-gray-700"}`}
            />

            {/* Header */}
            <button
              onClick={() => setActive(isOpen ? null : index)}
              className="w-full flex items-center justify-between px-6 py-5 text-left group"
            >
              <span className="text-base md:text-lg font-semibold text-white group-hover:text-blue-400 transition">
                {item.title}
              </span>

              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-blue-400"
              >
                {isOpen ? <FiMinus size={18} /> : <FiPlus size={18} />}
              </motion.span>
            </button>

            {/* Content */}
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="px-6 pb-6 text-sm md:text-base text-gray-300 leading-relaxed whitespace-pre-line"
                >
                  {item.content}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
